import {
  Scene, Mesh, ConeGeometry, MeshPhongMaterial,
  Vector3, Color, Group, Object3D,
} from 'three';

const MISSILE_SPEED     = 350;   // m/s（プレイヤーより遅い → 加速で逃げられる）
const MISSILE_MAX_SPEED = 420;
const MISSILE_RANGE     = 2500;
const HIT_RADIUS        = 12;
const TURN_RATE         = 1.6;   // 低旋回率（旋回で回避しやすい）
const MISSILE_DAMAGE    = 40;
const BURN_TIME         = 5.5;   // 長めの追尾時間（ゆっくり迫る緊張感）
const LAUNCH_DELAY      = 0.8;   // 発射後の加速猶予（最初は遅い）

interface EnemyMissileInst {
  mesh: Group;
  velocity: Vector3;
  life: number;
  burnTime: number;
  launchDelay: number;  // 発射直後の加速猶予
  active: boolean;
  trail: TrailParticle[];
}

interface TrailParticle {
  mesh: Mesh;
  life: number;
}

/**
 * 敵ミサイルシステム — プレイヤーを追尾するミサイル
 *
 * プレイヤーミサイルより旋回率が低く、加速・ブーストで回避可能。
 * 燃焼時間を過ぎると追尾が止まり直進する。
 */
export class EnemyMissileSystem {
  private missiles: EnemyMissileInst[] = [];
  private playerRef: Object3D | null = null;

  private missileMat = new MeshPhongMaterial({ color: new Color(0xff6644) });
  private trailMat = new MeshPhongMaterial({
    color: new Color(0xffaa88), transparent: true, opacity: 0.5,
  });

  constructor(private scene: Scene) {}

  setPlayerRef(player: Object3D): void {
    this.playerRef = player;
  }

  fire(from: Vector3, dir: Vector3): void {
    const group = new Group();
    const body = new Mesh(new ConeGeometry(0.25, 1.0, 5), this.missileMat);
    body.rotation.x = Math.PI / 2;
    group.add(body);
    group.position.copy(from);

    // 発射直後は低速（猶予時間中に加速）
    const initialSpeed = MISSILE_SPEED * 0.4;
    const vel = dir.clone().normalize().multiplyScalar(initialSpeed);
    group.lookAt(group.position.clone().add(vel));
    this.scene.add(group);

    this.missiles.push({
      mesh: group,
      velocity: vel,
      life: MISSILE_RANGE / (MISSILE_SPEED * 0.4),
      burnTime: BURN_TIME,
      launchDelay: LAUNCH_DELAY,
      active: true,
      trail: [],
    });
  }

  update(delta: number): void {
    for (const m of this.missiles) {
      if (!m.active) continue;

      m.life -= delta;
      m.burnTime -= delta;
      m.launchDelay -= delta;
      if (m.life <= 0) { this.deactivate(m); continue; }

      // 追尾 (燃焼時間内のみ)
      if (m.burnTime > 0 && this.playerRef) {
        const toPlayer = this.playerRef.position.clone().sub(m.mesh.position).normalize();
        const currentDir = m.velocity.clone().normalize();

        // 発射猶予中は旋回が弱く加速も遅い
        const turnMul = m.launchDelay > 0 ? 0.3 : 1.0;
        const accelRate = m.launchDelay > 0 ? 100 : 200;

        currentDir.lerp(toPlayer, Math.min(TURN_RATE * turnMul * delta, 1));
        currentDir.normalize();

        const currentSpeed = Math.min(m.velocity.length() + accelRate * delta, MISSILE_MAX_SPEED);
        m.velocity.copy(currentDir).multiplyScalar(currentSpeed);
      }

      m.mesh.position.addScaledVector(m.velocity, delta);

      if (m.velocity.lengthSq() > 0) {
        m.mesh.lookAt(m.mesh.position.clone().add(m.velocity));
      }

      // 地面衝突
      if (m.mesh.position.y < 5) { this.deactivate(m); continue; }

      // トレイル
      if (Math.random() < 0.5) {
        const t = new Mesh(
          new ConeGeometry(0.3 + Math.random() * 0.2, 1.2, 4),
          this.trailMat.clone()
        );
        t.position.copy(m.mesh.position);
        this.scene.add(t);
        m.trail.push({ mesh: t, life: 0.35 });
      }

      // トレイル更新
      for (let i = m.trail.length - 1; i >= 0; i--) {
        const t = m.trail[i];
        t.life -= delta;
        const mat = t.mesh.material as MeshPhongMaterial;
        mat.opacity = Math.max(0, t.life / 0.35 * 0.5);
        t.mesh.scale.multiplyScalar(1 + delta * 2.5);
        if (t.life <= 0) {
          this.scene.remove(t.mesh);
          t.mesh.geometry.dispose();
          m.trail.splice(i, 1);
        }
      }
    }
  }

  /** プレイヤーとの当たり判定。当たったらダメージを返す */
  checkPlayerHit(playerPos: Vector3, playerRadius: number): { damage: number; pos: Vector3 } | null {
    for (const m of this.missiles) {
      if (!m.active) continue;
      if (m.mesh.position.distanceTo(playerPos) < HIT_RADIUS + playerRadius) {
        const pos = m.mesh.position.clone();
        this.deactivate(m);
        return { damage: MISSILE_DAMAGE, pos };
      }
    }
    return null;
  }

  /** アクティブなミサイル位置一覧 (HUD警告用) */
  getActiveMissiles(): Vector3[] {
    return this.missiles
      .filter(m => m.active)
      .map(m => m.mesh.position.clone());
  }

  get activeMissileCount(): number {
    return this.missiles.filter(m => m.active).length;
  }

  private deactivate(m: EnemyMissileInst): void {
    m.active = false;
    this.scene.remove(m.mesh);
    for (const t of m.trail) {
      this.scene.remove(t.mesh);
      t.mesh.geometry.dispose();
    }
    m.trail = [];
  }

  dispose(): void {
    for (const m of this.missiles) {
      this.deactivate(m);
    }
    this.missiles = [];
  }
}
